import React from 'react';
import { Col, DatePicker, Form, Input, InputNumber, Row, Select, Switch, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { FormInstance } from 'antd';
import type { EmpleadosCatalogos, RolCatalogo } from '../../api/empleadosApi';
import { FctAviso } from '../../fct/react';
import { AVISO_ROLES, ROLES_EN_EDICION, ayuda } from './ayudas';
import type { ClaveAyuda } from './ayudas';
import './EmpleadoForm.css';
import { DOMINIO_EMAIL, LIMITES, MAX_ENTERO, errorEmail, opcionesInterlocucion, opcionesRoles } from './reglas';

const max = (n: number, texto: string) => ({ max: n, message: `${texto} no puede superar ${n} caracteres.` });

// Anchos por pantalla: a una columna en estrecho, a dos en medio; desde «md», cuatro o tres campos por fila según la tarjeta.
const CUARTO = { xs: 24, sm: 12, md: 6 };
const TERCIO = { xs: 24, sm: 12, md: 8 };
const COMPLETA = { xs: 24 };

/** La ayuda de un campo, en un icono de información con su texto en un `Tooltip` (al pasar el ratón y al enfocar con el teclado). */
const Info: React.FC<{ clave: ClaveAyuda }> = ({ clave }) => (
  <Tooltip title={ayuda(clave)}>
    <InfoCircleOutlined className="empleado-info" tabIndex={0} role="img" aria-label="Ayuda" data-ayuda={clave} />
  </Tooltip>
);

/** Etiqueta de un campo con su icono de ayuda. */
const etiqueta = (texto: string, clave: ClaveAyuda) => <>{texto}<Info clave={clave} /></>;

/** Un bloque del formulario con su título. `fieldset` y `legend` lo anuncian como grupo a los lectores de pantalla. */
export const Seccion: React.FC<{ titulo: string; children: React.ReactNode }> = ({ titulo, children }) => (
  <fieldset className="empleado-seccion">
    <legend className="empleado-seccion__titulo">{titulo}</legend>
    {children}
  </fieldset>
);

/** Interruptor con su etiqueta y su icono de ayuda al lado. La ayuda sale de `ayudas.ts`. */
const Marca: React.FC<{ nombre: ClaveAyuda; texto: string }> = ({ nombre, texto }) => {
  const id = `empleado-${nombre}`;
  return (
    <div className="empleado-marca">
      <Form.Item name={nombre} valuePropName="checked" noStyle>
        <Switch id={id} size="small" />
      </Form.Item>
      <label htmlFor={id} className="empleado-marca__etiqueta">{texto}</label>
      <Info clave={nombre} />
    </div>
  );
};

/** Aviso fijo de todo lo que edita roles: la tabla de Gescom no es la pertenencia a los grupos de Entra. */
export const AvisoRoles: React.FC = () => (
  <div style={{ marginBottom: 8 }}>
    <FctAviso tipo="aviso" titulo={AVISO_ROLES.titulo}>{AVISO_ROLES.texto}</FctAviso>
  </div>
);

/** Selector de roles por su nombre; si un rol no tiene nombre, se ve su código y se conserva. */
export const SelectorRoles: React.FC<{
  catalogo: RolCatalogo[];
  /** Los roles que ya tiene el empleado, por si alguno no está en el catálogo. */
  actuales?: string[];
  /** Una sola línea: los roles de más se resumen en «+N». */
  enUnaLinea?: boolean;
  id?: string;
  value?: string[];
  onChange?: (roles: string[]) => void;
}> = ({ catalogo, actuales, enUnaLinea, ...resto }) => (
  <Select
    {...resto}
    mode="multiple"
    allowClear
    maxTagCount={enUnaLinea ? 'responsive' : undefined}
    optionFilterProp="label"
    placeholder="Sin roles"
    options={opcionesRoles(catalogo, actuales)}
  />
);

/**
 * Campos profesionales de CONTRATO.md §2, comunes al alta y a la edición, en tres tarjetas compactas. Los obligatorios
 * llevan su regla y los anchos máximos son los del servidor. La zona se filtra por la delegación elegida. Los roles son
 * una línea al final de «Niveles y permisos»: selector en el alta, una frase en la edición.
 */
export const CamposEmpleado: React.FC<{
  form: FormInstance;
  catalogos: EmpleadosCatalogos;
  /** El alta añade la fecha de alta (sin valor, hoy) y el selector de roles. */
  conFechaAlta?: boolean;
  /** Nivel de interlocución con el que se abre la ficha: si no es bajo, medio ni alto, se sigue ofreciendo. */
  interlocucionInicial?: number | null;
}> = ({ form, catalogos, conFechaAlta, interlocucionInicial }) => {
  const idDelegacion = Form.useWatch('idDelegacion', form);
  const zonas = catalogos.zonas.filter((z) => z.idDelegacion === idDelegacion);

  return (
    <>
      <Seccion titulo="Identidad">
        <Row gutter={[12, 0]}>
          <Col {...CUARTO}>
            <Form.Item name="nombreCompleto" label={etiqueta('Nombre completo', 'nombreCompleto')}
              rules={[{ required: true, whitespace: true, message: 'El nombre es obligatorio.' }, max(LIMITES.nombreCompleto, 'El nombre')]}>
              <Input autoComplete="off" autoFocus />
            </Form.Item>
          </Col>
          <Col {...CUARTO}>
            <Form.Item name="emailEmpresarial" label={etiqueta('Email', 'emailEmpresarial')} required
              rules={[{ validator: (_, v) => { const e = errorEmail(v); return e ? Promise.reject(new Error(e)) : Promise.resolve(); } }]}>
              <Input autoComplete="off" inputMode="email" placeholder={`nombre${DOMINIO_EMAIL}`} />
            </Form.Item>
          </Col>
          <Col {...CUARTO}>
            <Form.Item name="codigoCorto" label={etiqueta('Código corto', 'codigoCorto')} rules={[max(LIMITES.codigoCorto, 'El código corto')]}>
              <Input autoComplete="off" />
            </Form.Item>
          </Col>
          <Col {...CUARTO}>
            <Form.Item name="nombreAbreviado" label={etiqueta('Nombre abreviado', 'nombreAbreviado')} rules={[max(LIMITES.nombreAbreviado, 'El nombre abreviado')]}>
              <Input autoComplete="off" />
            </Form.Item>
          </Col>
        </Row>
      </Seccion>

      <Seccion titulo="Puesto y ubicación">
        <Row gutter={[12, 0]}>
          <Col {...TERCIO}>
            <Form.Item name="puesto" label={etiqueta('Puesto', 'puesto')} rules={[max(LIMITES.puesto, 'El puesto')]}>
              <Input autoComplete="off" />
            </Form.Item>
          </Col>
          <Col {...TERCIO}>
            <Form.Item name="departamento" label={etiqueta('Departamento', 'departamento')} rules={[max(LIMITES.departamento, 'El departamento')]}>
              <Input autoComplete="off" />
            </Form.Item>
          </Col>
          <Col {...TERCIO}>
            <Form.Item name="telefono" label={etiqueta('Teléfono corporativo', 'telefono')} rules={[max(LIMITES.telefono, 'El teléfono')]}>
              <Input autoComplete="off" inputMode="tel" />
            </Form.Item>
          </Col>
          <Col {...TERCIO}>
            <Form.Item name="idDelegacion" label={etiqueta('Delegación', 'idDelegacion')} rules={[{ required: true, message: 'La delegación es obligatoria.' }]}>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Elige una delegación"
                options={catalogos.delegaciones.map((d) => ({ value: d.idDelegacion, label: d.descripcion }))}
                onChange={() => form.setFieldValue('idZona', undefined)}
              />
            </Form.Item>
          </Col>
          <Col {...TERCIO}>
            <Form.Item name="idZona" label={etiqueta('Zona', 'idZona')}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                disabled={!idDelegacion}
                placeholder={idDelegacion ? 'Elige una zona' : 'Elige antes la delegación'}
                options={zonas.map((z) => ({ value: z.idZona, label: z.descripcion }))}
              />
            </Form.Item>
          </Col>
          {conFechaAlta && (
            <Col {...TERCIO}>
              <Form.Item name="fechaAlta" label={etiqueta('Fecha de alta', 'fechaAlta')}>
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          )}
        </Row>
      </Seccion>

      <Seccion titulo="Niveles y permisos">
        <Row gutter={[12, 0]}>
          <Col {...TERCIO}>
            <Form.Item name="nivelSeguridad" label={etiqueta('Nivel de seguridad', 'nivelSeguridad')} rules={[{ required: true, message: 'El nivel de seguridad es obligatorio.' }]}>
              <InputNumber max={MAX_ENTERO} precision={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col {...TERCIO}>
            <Form.Item name="nivelAsesor" label={etiqueta('Nivel de asesor', 'nivelAsesor')}>
              <InputNumber max={MAX_ENTERO} precision={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col {...TERCIO}>
            <Form.Item name="nivelInterlocucion" label={etiqueta('Nivel de interlocución', 'nivelInterlocucion')}>
              <Select allowClear placeholder="Sin nivel" options={opcionesInterlocucion(interlocucionInicial)} />
            </Form.Item>
          </Col>
          <Col {...TERCIO}><Marca nombre="accesoGdgo" texto="Acceso a GDGO" /></Col>
          <Col {...TERCIO}><Marca nombre="asignarCcl" texto="Asignar CCL" /></Col>
          <Col {...TERCIO}><Marca nombre="controlAcompaniamiento" texto="Control de acompañamiento" /></Col>
          <Col {...COMPLETA}>
            {conFechaAlta ? (
              <>
                <AvisoRoles />
                <Form.Item name="roles" label={etiqueta('Roles', 'roles')}>
                  <SelectorRoles catalogo={catalogos.roles} enUnaLinea />
                </Form.Item>
              </>
            ) : (
              <p className="empleado-roles__linea">{ROLES_EN_EDICION}</p>
            )}
          </Col>
        </Row>
      </Seccion>
    </>
  );
};
